// src/pages/errors/NotFound404.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PongTwoPaddlesGame from "./games/PongTwoPaddlesGame.jsx";

/* ——— короткий «вууш» ——— */
function playWhoosh() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const duration = 1.1, now = ctx.currentTime;
    const noise = ctx.createBufferSource();
    const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 1.5);
    noise.buffer = buffer;
    const biquad = ctx.createBiquadFilter(); biquad.type = "bandpass"; biquad.frequency.setValueAtTime(380, now); biquad.Q.value = 0.6;
    const gain = ctx.createGain(); gain.gain.setValueAtTime(0.0001, now); gain.gain.exponentialRampToValueAtTime(0.4, now + 0.08); gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    noise.connect(biquad).connect(gain).connect(ctx.destination);
    noise.start(); noise.stop(now + duration); setTimeout(() => ctx.close(), (duration + 0.1) * 1000);
  } catch {}
}

/* ——— визуал «чёрной дыры» ——— */
const BlackHole = ({ size = 180 }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <div className="absolute inset-[-28%] rounded-full blur-2xl bg-violet-600/25" />
    <motion.div
      className="absolute inset-[-18%] rounded-full"
      style={{ background:"conic-gradient(from 0deg, rgba(168,85,247,0.00) 0%, rgba(168,85,247,0.35) 20%, rgba(236,72,153,0.25) 40%, rgba(168,85,247,0.35) 60%, rgba(168,85,247,0.00) 100%)", filter:"blur(10px)" }}
      animate={{ rotate: 360 }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
    />
    <div className="absolute inset-0 rounded-full bg-black" />
    <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white/5 to-black/20" />
    <div className="absolute inset-0 rounded-full ring-2 ring-violet-500/25" />
  </div>
);

/* ——— буква, улетающая к центру ——— */
const SpiralLetter = ({ ch, angle, startR = 180, flight = 1.8, delay = 0 }) => {
  const rad = (angle * Math.PI) / 180, x0 = Math.cos(rad) * startR, y0 = Math.sin(rad) * startR;
  return (
    <motion.span
      className="absolute left-1/2 top-1/2 text-3xl font-extrabold select-none"
      style={{ textShadow: "0 0 12px rgba(139,92,246,.7)" }}
      initial={{ x: x0, y: y0, opacity: 0 }}
      animate={{ x: [x0, x0 * 0.5, 0], y: [y0, y0 * 0.5, 0], rotate: [0, 120, 260], opacity: [0, 1, 0], scale: [1, 0.95, 0.2] }}
      transition={{ duration: flight, times: [0, 0.65, 1], delay, ease: "easeInOut" }}
    >
      <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-fuchsia-400 to-violet-400">{ch}</span>
    </motion.span>
  );
};

const NotFound404 = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [easter, setEaster] = useState(false);
  const [wave, setWave] = useState(0);
  const [showGame, setShowGame] = useState(false);

  const brand = "SES";
  const letters = useMemo(() => {
    const COUNT = 22;
    return Array.from({ length: COUNT }, (_, i) => ({
      ch: brand[i % brand.length],
      angle: (i * 360) / COUNT,
      delay: i * 0.06,
    }));
  }, [brand, wave]);

  const triggerEaster = () => {
    setEaster(true);
    setWave((w) => w + 1);
    playWhoosh();
  };

  // Space/Esc для игры
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") { e.preventDefault(); setShowGame(true); }
      if (e.key === "Escape") setShowGame(false);
    };
    window.addEventListener("keydown", onKey, { passive: false });
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Отключаем ГЛОБАЛЬНЫЙ фон целиком и рисуем локальную сетку
  useEffect(() => {
    document.body.classList.add("no-cyber-bg");
    return () => document.body.classList.remove("no-cyber-bg");
  }, []);

  // Яркость локальной сетки на 404 — такая же, как в глобальном фоне
  const MAIN_ALPHA = 0.22;
  const SUB_ALPHA  = 0.12;

  return (
    <div className="relative min-h-screen w-full grid place-items-center p-6 overflow-hidden bg-transparent text-white">
      {/* Локальная СЕТКА БЕЗ маски: перекрывает ВСЁ окно */}
      <div className="fixed inset-0 -z-10">
        {/* подложка */}
        <div className="absolute inset-0 bg-slate-950" />
        {/* верхний радиальный градиент (можно убрать при желании) */}
        <div className="absolute inset-0 [background-image:radial-gradient(1200px_520px_at_50%_-10%,rgba(139,92,246,0.32),transparent)]" />
        {/* собственно линии сетки */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: [
              `linear-gradient(rgba(255,255,255,${MAIN_ALPHA}) 1px, transparent 1px)`,
              `linear-gradient(90deg, rgba(255,255,255,${MAIN_ALPHA}) 1px, transparent 1px)`,
              `linear-gradient(rgba(255,255,255,${SUB_ALPHA}) 1px, transparent 1px)`,
              `linear-gradient(90deg, rgba(255,255,255,${SUB_ALPHA}) 1px, transparent 1px)`,
            ].join(","),
            backgroundSize: "32px 32px, 32px 32px, 8px 8px, 8px 8px",
            backgroundPosition: "0 0, 0 0, 0 0, 0 0",
          }}
        />
        {/* нижний затемняющий градиент */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      {/* контент */}
      <div className="relative z-10 flex flex-col items-center gap-6 text-center" role="main">
        <div className="relative w-[420px] max-w-full h-[300px]">
          {/* волна букв */}
          <AnimatePresence initial={false}>
            {easter && (
              <div className="absolute inset-0">
                {letters.map((l, i) => (
                  <SpiralLetter key={`${wave}-${i}`} ch={l.ch} angle={l.angle} startR={190} flight={1.7} delay={l.delay} />
                ))}
              </div>
            )}
          </AnimatePresence>

          {/* «чёрная дыра» */}
          <button
            type="button"
            onClick={triggerEaster}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 outline-none"
            title="Кликни — волна букв"
          >
            <BlackHole size={190} />
          </button>
        </div>

        <motion.h1 className="text-3xl sm:text-4xl font-bold" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          404 — Страница потерялась во вселенной
        </motion.h1>

        <motion.p className="max-w-xl text-white/80" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
          Мы не нашли <code className="px-1 rounded bg-white/10">{pathname}</code>. Клик по «чёрной дыре» запускает эффектную волну букв.{" "}
          <strong>Нажми пробел</strong>, чтобы открыть мини-игру. <span className="text-white/60">(Esc — закрыть)</span>
        </motion.p>

        <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
          <button onClick={() => navigate("/feed")} className="rounded-xl px-4 py-2 bg-violet-600 hover:bg-violet-700 transition">На ленту</button>
          <button onClick={() => navigate("/")} className="rounded-xl px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/15 transition">К странице входа</button>
          <button onClick={() => (window.history.length > 1 ? window.history.back() : navigate("/feed"))} className="rounded-xl px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/15 transition">Назад</button>
        </div>

        <motion.div className="text-xs text-white/60 mt-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
          Если это ошибка приложения — напишите нам через профиль → «Баги».
        </motion.div>
      </div>

      {/* Оверлей с игрой (Space / Esc) */}
      <AnimatePresence>
        {showGame && (
          <motion.div
            className="fixed inset-0 z-[100] grid place-items-center bg-black/60 backdrop-blur-md p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="relative">
              <button
                onClick={() => setShowGame(false)}
                className="absolute -top-3 -right-3 z-10 h-8 w-8 rounded-full bg-white/10 border border-white/20 hover:bg-white/20"
                title="Закрыть (Esc)"
              >
                ✕
              </button>
              <PongTwoPaddlesGame width={720} height={420} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotFound404;
